import React, { useState } from 'react';
import { Box, Button, Icon, Modal } from '@neshane/blueberry';
import { useCompensationJobs, useCompensationJob } from '../hooks';
import { CompensationStatusBadge } from '../components/StatusBadges';
import { COMPENSATION_JOB_TYPE_LABELS, COMPENSATION_STATUS_LABELS, formatDateTime } from '../labels';
import { PageEmptyState, Spinner } from '../../../common/components';

const STATUS_OPTIONS = Object.keys(COMPENSATION_STATUS_LABELS);

function CompensationJobsContainer() {
    const [status, setStatus] = useState('');
    const [detailId, setDetailId] = useState(null);
    const { jobs, total, loading, error, refresh } = useCompensationJobs({ status: status || undefined });
    const detail = useCompensationJob(detailId);

    if (error) {
        return <p className="body-m text-secondary-alert-default">{error}</p>;
    }

    return (
        <div className="flex flex-col gap-24">
            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-4">
                    <h1 className="heading-l text-primary">کارهای جبرانی</h1>
                    <p className="body-m text-secondary-bg-primary">جبران خودکار خرابی‌های جریان‌های حیاتی (تعداد کل: {total})</p>
                </div>
                <Button text="بروزرسانی" iconName="arrow-path" variant="secondaryNeutral" onClick={refresh} />
            </div>

            <Box className="flex flex-wrap items-end gap-16">
                <div className="flex flex-col gap-4">
                    <label className="body-s text-secondary-bg-primary">وضعیت</label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="rounded-12 border border-default p-8" title="لطفا این فیلد را پر کنید" required>
                        <option value="">همه</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{COMPENSATION_STATUS_LABELS[s]}</option>
                        ))}
                    </select>
                </div>
            </Box>

            {loading ? (
                <div className="flex flex-col items-center justify-center gap-16 py-64 text-secondary-bg-primary">
                    <Spinner />
                    <p className="body-m">در حال بارگذاری کارهای جبرانی...</p>
                </div>
            ) : jobs.length === 0 ? (
                <PageEmptyState
                    iconName="arrow-path"
                    iconVariant="success"
                    title="کار جبرانی در انتظار نیست"
                    description="هیچ کار جبرانی با فیلترهای فعلی یافت نشد"
                />
            ) : (
                <div className="flex flex-col gap-16">
                    {jobs.map((job) => (
                        <Box key={job.id} className="flex flex-col gap-16">
                            <div className="flex items-center gap-16">
                                <span className="flex items-center justify-center rounded-12 bg-secondary-warning-default p-8">
                                    <Icon name="arrow-path" size="m" />
                                </span>
                                <div className="flex flex-1 flex-col gap-4">
                                    <h3 className="title-m text-primary">{COMPENSATION_JOB_TYPE_LABELS[job.jobType] || job.jobType}</h3>
                                    <p className="body-s text-secondary-bg-primary">اقدام: {job.action}</p>
                                </div>
                                <CompensationStatusBadge status={job.status} />
                            </div>

                            <div className="flex flex-col gap-8">
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">شناسه مرجع:</span>
                                    <span className="body-m text-primary" dir="ltr">{job.referenceId}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">کاربر:</span>
                                    <span className="body-m text-primary">{job.userId ?? '—'}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">تلاش‌ها:</span>
                                    <span className="body-m text-primary">{job.attemptCount}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">اجرای بعدی:</span>
                                    <span className="body-m text-primary">{formatDateTime(job.nextRunAt)}</span>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    text="جزئیات"
                                    variant="secondaryBrand"
                                    iconName="circle-question-filled"
                                    onClick={() => setDetailId(job.id)}
                                />
                            </div>
                        </Box>
                    ))}
                </div>
            )}

            {detailId && (
                <Modal
                    iconName="circle-question-filled"
                    iconVariant="purple"
                    title="جزئیات کار جبرانی"
                    subtitle={detail.job ? COMPENSATION_JOB_TYPE_LABELS[detail.job.jobType] || detail.job.jobType : undefined}
                    isOpen
                    onClose={() => setDetailId(null)}
                    actions={
                        <div className="flex justify-end">
                            <Button text="بستن" variant="secondaryNeutral" onClick={() => setDetailId(null)} />
                        </div>
                    }
                >
                    {detail.loading ? (
                        <div className="flex justify-center py-16"><Spinner /></div>
                    ) : detail.job ? (
                        <div className="flex flex-col gap-16">
                            <div className="flex items-center justify-between">
                                <CompensationStatusBadge status={detail.job.status} />
                            </div>
                            <div className="flex flex-col gap-8">
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">شناسه مرجع:</span>
                                    <span className="body-m text-primary" dir="ltr">{detail.job.referenceId}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">کاربر:</span>
                                    <span className="body-m text-primary">{detail.job.userId ?? '—'}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">اقدام:</span>
                                    <span className="body-m text-primary">{detail.job.action}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">تلاش‌ها:</span>
                                    <span className="body-m text-primary">{detail.job.attemptCount}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">ایجاد:</span>
                                    <span className="body-m text-primary">{formatDateTime(detail.job.createdAt)}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">اجرای بعدی:</span>
                                    <span className="body-m text-primary">{formatDateTime(detail.job.nextRunAt)}</span>
                                </div>
                            </div>
                            {detail.job.lastErrorMessage && (
                                <div className="rounded-12 bg-secondary-alert-default p-12">
                                    <p className="body-s text-secondary-alert-default">{detail.job.lastErrorMessage}</p>
                                </div>
                            )}
                            {detail.job.payloadRef && (
                                <pre className="rounded-12 bg-secondary-default p-12 body-s text-primary overflow-x-auto" dir="ltr">
                                    {JSON.stringify(detail.job.payloadRef, null, 2)}
                                </pre>
                            )}
                        </div>
                    ) : (
                        <p className="body-m text-secondary-alert-default">کار جبرانی یافت نشد</p>
                    )}
                </Modal>
            )}
        </div>
    );
}

export default CompensationJobsContainer;
